// @ts-check
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { TrackInsertMarkName, TrackDeleteMarkName, TrackFormatMarkName } from '../constants.js';
import { getTrackChanges } from '../trackChangesHelpers/getTrackChanges.js';
import { enumerateStructuralRowChanges } from '../trackChangesHelpers/structuralRowChanges.js';

export const TrackChangesBasePluginKey = new PluginKey('TrackChangesBase');

export const TrackChangesBasePlugin = () => {
  return new Plugin({
    key: TrackChangesBasePluginKey,

    state: {
      init(_, state) {
        const decorations = getTrackChangesDecorations(state, false, false);
        return {
          isTrackChangesActive: false,
          onlyOriginalShown: false,
          onlyModifiedShown: false,
          pendingDeadKeyPlaceholder: null,
          decorations,
        };
      },

      apply(tr, oldState, prevEditorState, newEditorState) {
        const meta = tr.getMeta(TrackChangesBasePluginKey);
        const pendingDeadKeyPlaceholder =
          meta && Object.prototype.hasOwnProperty.call(meta, 'pendingDeadKeyPlaceholder')
            ? meta.pendingDeadKeyPlaceholder
            : oldState.pendingDeadKeyPlaceholder;

        // Handle meta commands that require full recalculation
        if (meta && meta.type === 'TRACK_CHANGES_ENABLE') {
          return {
            ...oldState,
            isTrackChangesActive: meta.value === true,
            pendingDeadKeyPlaceholder,
            decorations: getTrackChangesDecorations(
              newEditorState,
              oldState.onlyOriginalShown,
              oldState.onlyModifiedShown,
            ),
          };
        }

        if (meta && meta.type === 'SHOW_ONLY_ORIGINAL') {
          return {
            ...oldState,
            onlyOriginalShown: meta.value === true,
            onlyModifiedShown: false,
            pendingDeadKeyPlaceholder,
            decorations: getTrackChangesDecorations(newEditorState, meta.value === true, false),
          };
        }

        if (meta && meta.type === 'SHOW_ONLY_MODIFIED') {
          return {
            ...oldState,
            onlyOriginalShown: false,
            onlyModifiedShown: meta.value === true,
            pendingDeadKeyPlaceholder,
            decorations: getTrackChangesDecorations(newEditorState, false, meta.value === true),
          };
        }

        if (!tr.docChanged) {
          return {
            ...oldState,
            pendingDeadKeyPlaceholder,
          };
        }

        if (!meta) {
          let mightAffectTrackChanges = false;

          tr.steps.forEach((step) => {
            // Only Replace(Around)Step carry slice/from/to; the base Step type
            // does not expose them, so narrow before reading.
            const replaceStep = /** @type {{ slice?: unknown, from?: number, to?: number }} */ (step);
            if (replaceStep.slice || replaceStep.from !== replaceStep.to) {
              mightAffectTrackChanges = true;
            }
          });

          if (mightAffectTrackChanges) {
            return {
              ...oldState,
              pendingDeadKeyPlaceholder,
              decorations: getTrackChangesDecorations(
                newEditorState,
                oldState.onlyOriginalShown,
                oldState.onlyModifiedShown,
              ),
            };
          }

          // No changes that affect track changes, just map decorations
          return {
            ...oldState,
            pendingDeadKeyPlaceholder,
            decorations: oldState.decorations.map(tr.mapping, tr.doc),
          };
        }

        return {
          ...oldState,
          pendingDeadKeyPlaceholder,
          decorations: getTrackChangesDecorations(
            newEditorState,
            oldState.onlyOriginalShown,
            oldState.onlyModifiedShown,
          ),
        };
      },
    },

    props: {
      decorations(state) {
        return this.getState(state)?.decorations;
      },
    },
  });
};

const getTrackChangesDecorations = (state, onlyOriginalShown, onlyModifiedShown) => {
  if (!state.doc || !state.doc.nodeSize || (onlyModifiedShown && onlyOriginalShown)) {
    return DecorationSet.empty;
  }

  const decorations = [];
  const trackedChanges = getTrackChanges(state);

  // Structural row revisions (whole-table insert/delete) live on tableRow node
  // attributes, not inline marks, so they are decorated separately as node
  // decorations on each tracked <tr>. Done here (not in a separate plugin) so the
  // show-original / show-modified modes and styling stay in one place.
  addStructuralRowDecorations(decorations, state, onlyOriginalShown, onlyModifiedShown);

  if (!trackedChanges.length) {
    return decorations.length ? DecorationSet.create(state.doc, decorations) : DecorationSet.empty;
  }

  trackedChanges.forEach(({ mark, from, to }) => {
    if (mark.type.name === TrackInsertMarkName) {
      if (onlyOriginalShown) {
        const decoration = Decoration.inline(from, to, {
          class: 'track-insert-dec hidden',
        });
        decorations.push(decoration);
      } else if (onlyModifiedShown) {
        const decoration = Decoration.inline(from, to, {
          class: 'track-insert-dec normal',
        });
        decorations.push(decoration);
      } else {
        const decoration = Decoration.inline(from, to, {
          class: 'track-insert-dec highlighted',
        });
        decorations.push(decoration);
      }
    }

    if (mark.type.name === TrackDeleteMarkName) {
      if (onlyOriginalShown) {
        const decoration = Decoration.inline(from, to, {
          class: 'track-delete-dec normal',
        });
        decorations.push(decoration);
      } else if (onlyModifiedShown) {
        const decoration = Decoration.inline(from, to, {
          class: 'track-delete-dec hidden',
        });
        decorations.push(decoration);
      } else {
        const decorationInline = Decoration.inline(from, to, {
          class: 'track-delete-dec highlighted', // 'hidden'
        });
        decorations.push(decorationInline);

        // Workaround for Chrome to handle text deletion correctly.
        // Maybe this won't be needed later.
        const decorationWidget = Decoration.widget(
          from,
          () => {
            const span = document.createElement('span');
            span.classList.add('track-delete-widget');
            return span;
          },
          {
            ignoreSelection: true,
            key: 'stable-key',
          },
        );
        decorations.push(decorationWidget);
      }
    }

    if (mark.type.name === TrackFormatMarkName) {
      if (onlyOriginalShown) {
        // For this we should render the before array as marks.
        // No solid idea for this yet.
        const decoration = Decoration.inline(from, to, {
          class: 'track-format-dec before',
        });
        decorations.push(decoration);
      } else if (onlyModifiedShown) {
        // For this we should render nothing.
        // We already have the applied marks on the text.
        const decoration = Decoration.inline(from, to, {
          class: 'track-format-dec normal',
        });
        decorations.push(decoration);
      } else {
        const decoration = Decoration.inline(from, to, {
          class: 'track-format-dec highlighted',
        });
        decorations.push(decoration);
      }
    }
  });

  return DecorationSet.create(state.doc, decorations);
};

/**
 * Append node decorations for structural row revisions (whole-table
 * insert/delete) to `decorations`. Each tracked `<tr>` gets a
 * `track-row-insert-dec` / `track-row-delete-dec` class plus the same
 * `highlighted` / `hidden` / `normal` mode the inline marks use, so insertions
 * and deletions read consistently across inline and structural changes.
 *
 * @param {Array<import('prosemirror-view').Decoration>} decorations
 * @param {import('prosemirror-state').EditorState} state
 * @param {boolean} onlyOriginalShown
 * @param {boolean} onlyModifiedShown
 */
const addStructuralRowDecorations = (decorations, state, onlyOriginalShown, onlyModifiedShown) => {
  const structuralChanges = enumerateStructuralRowChanges(state);
  if (!structuralChanges.length) return;

  for (const change of structuralChanges) {
    const isInsert = change.side === 'insertion';
    const baseClass = isInsert ? 'track-row-insert-dec' : 'track-row-delete-dec';

    // Mode mirrors the inline marks: an insertion is hidden in the original view
    // and plain in the modified view; a deletion is plain in the original view
    // and hidden in the modified view.
    let mode = 'highlighted';
    if (onlyOriginalShown) mode = isInsert ? 'hidden' : 'normal';
    else if (onlyModifiedShown) mode = isInsert ? 'normal' : 'hidden';

    for (const row of change.rows) {
      decorations.push(
        Decoration.node(row.from, row.to, {
          class: `${baseClass} ${mode}`,
          'data-track-change-id': change.id,
          'data-track-change-kind': change.subtype,
          'data-track-change-author': change.author || '',
          'data-track-change-date': change.date || '',
        }),
      );
    }
  }
};
